import { Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { first, map, startWith } from 'rxjs/operators';
import { Brand, Company, ListBrandsRequest, ListBrandsResponse, Product2, ProductSpecification, ProductType, ProductWorkflow, ProductWorkflowState, QualityLevel, SituViewWebApiClient, WorkflowStateType } from 'situview-webapi-client';
import { ProductSelectorDialogComponent, ProductSpecificationTreeDialogComponent, ProductSpecificationTreeResultData, WorkflowTriggerDialogComponent, WorkflowTriggerResultData } from 'src/app/components';
import { AuthService, BreadcrumbService, CompanyViewService, PaginatedProducts, ProductService, StateService, Utils } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { CreateBrandDialogComponent } from '../../products/create-brand-dialog/create-brand-dialog/create-brand-dialog.component';


@Component({
  selector: 'product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.less'],
})
export class ProductDetailsComponent extends SituBaseComponent {

  private readonly defaultColumns = ['select', 'company', 'name', 'sku', 'productType', 'productSpecification', 'description', 'brand', 'qualityLevel', 'state', 'notes', 'calloutsEnabled', 'hideSku', 'enabled', 'edit'];
  public readonly Number = Number;
  public readonly QualityLevel = QualityLevel;

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  pageNumber = 1;
  pageSize = 100;
  totalItems = 0;

  products: ProductLineItem[] = [];
  allProductSpecifications: ProductSpecification[] = [];

  displayedColumns: string[] = this.defaultColumns;

  reportId = '';
  isReportReady = false;
  reportErrorCount = 0;
  reportPollCount = 0;

  companyIdFilter: string | undefined;
  companyNameFilter: string | undefined;
  productTypeFilter: string | undefined;
  productTypeNameFilter: string | undefined;
  productStateFilter: ProductWorkflowState | undefined;
  productStateNameFilter: string | undefined;
  searchTerm: string = "";

  workflowStateType = WorkflowStateType;

  bulkProductTypeIdFilter: string | undefined;
  bulkProductTypeNameFilter: string | undefined;
  bulkCalloutsEnabled: boolean | undefined;
  bulkHideProductCode: boolean | undefined;
  bulkProductEnabled: boolean | undefined;
    
  workflowTriggerDialogRef: MatDialogRef<WorkflowTriggerDialogComponent> | undefined;
  productSelectorDialogRef: MatDialogRef<ProductSelectorDialogComponent> | undefined;
  productSpecificationTreeDialogRef: MatDialogRef<ProductSpecificationTreeDialogComponent> | undefined;

  @ViewChild('paginator') paginator: MatPaginator| undefined;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private productService: ProductService,
    private situViewClient: SituViewWebApiClient,
    private companyViewService: CompanyViewService,
    private breadcrumbService: BreadcrumbService,
    private router: Router) {
      super(authService, stateService);

      
    this.filteredCompanies = this.companyControl.valueChanges.pipe(
      map((value) => {
        return this.filterCompanies(value);
      })
    );

    this.filteredProductTypes = this.productTypeControl.valueChanges.pipe(
      map((value) => {
        return this.filterProductTypes(value);
      })
    );

    this.filteredProductStates = this.productStateControl.valueChanges.pipe(
      map((value) => {
        return this.filterProductStates(value);
      })
    );

    this.filteredBulkProductTypes = this.bulkProductTypeControl.valueChanges.pipe(
      map((value) => {
        return this.filterProductTypes(value);
      })
    );

    this.filteredBulkBrands = this.bulkBrandControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterBrands(value);
      })
    );

    this.loadCompanies();
    this.loadProductStates();
    this.loadProductTypes();
    this.loadProductSpecifications();
    this.loadBrands();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  onSearch() {
    this.paginator?.firstPage();

    this.loadData();
  }

  onPageChange(evt: PageEvent) {
    this.pageSize = evt.pageSize;
    this.pageNumber = evt.pageIndex + 1;

    this.loadData();
  }

  loadData() {
    this.getProducts()
      .then((productPage: PaginatedProducts) => {
        this.products = productPage.products!.map(x => new ProductLineItem(x));
        this.totalItems = productPage.totalItems!;
      });
  }

  getProducts(loadAllProducts = false) : Promise<PaginatedProducts> {
    this.loading = true;

    if (loadAllProducts) {
      var pageNumber = 1;
      var pageSize = 1000000;
    } else {
      var pageNumber = this.pageNumber;
      var pageSize = this.pageSize;
    }

    var productLoadPromise = this.productService.LoadPaginatedProducts(
      this.selectedCompany?.id!,
      undefined,
      this.companyIdFilter,
      this.productTypeFilter,
      undefined,
      this.productStateFilter,
      this.searchTerm,
      undefined,
      undefined, //ranges
      true,
      true,
      false, //ranges
      true,
      undefined,
      undefined,
      pageNumber, pageSize
    )

    productLoadPromise.catch((error: any) => {
      console.log(error);
    })
    .finally(() => {
      this.loading = false;
    });

    return productLoadPromise;
  }

  downloadCsv() {
    var dataRows: any[][] = [['ID', 'Company', 'Name', 'Product Code', 'Product Type', 'Product Specification', 'Description', 'Brand', 'Quality Level', 'Current Workflow State', 'Notes', 'Callouts Enabled', 'Hide Sku', 'Enabled']];

    this.getProducts(true).then((allProducts: PaginatedProducts) => {

      allProducts.products?.forEach((x: Product2) => {
        const quality = x.qualityLevel
          ? QualityLevel[x.qualityLevel]
          : (x.brand?.qualityLevel ? '*' + QualityLevel[x.brand.qualityLevel] : null);
        dataRows.push([
          x.id ?? '',
          this.companyDirectory[x.id ?? ''] ?? '',
          x.name ?? '',
          x.sku ?? '',
          this.getProductTypeName(x) ?? '',
          this.getProductSpecificationString(x) ?? '',
          x.description,
          x.brand?.id ?? '',
          quality ?? '',
          this.getWorkflowState(x) ?? '',
          x.notes,
          x.calloutsEnabled ? 'TRUE' : 'FALSE',
          x.hideSku ? 'TRUE' : 'FALSE',
          !x.disabled ? 'TRUE' : 'FALSE',
        ]);
      });

      Utils.exportToCsv("product-details.csv", dataRows);
    })
  }

  canEditProductSpecifications(item: ProductLineItem) {
    var product = item.product;

    var hasProductSpecificationsToSelect = this.allProductSpecifications.some(x => x.productTypeId === product.productTypeId);
    return hasProductSpecificationsToSelect;
  }
  
  canBulkEditProductSpecifications() {
    var editModeProducts = this.products.filter(e => e.editMode);

    if (editModeProducts.length === 0) {
      return false;
    } else if (editModeProducts.length > 1) {
      var firstProduct = editModeProducts[0].product;
      var haveSameProductType = editModeProducts.every(e => e.product.productTypeId === firstProduct.productTypeId);

      if (!haveSameProductType) {
        return false;
      }
    }

    return this.canEditProductSpecifications(editModeProducts[0]);
  }

  onBulkOpenProductSpecificationDialog() {

    var editModeProducts = this.products.filter(e => e.editMode);
    var firstProduct = editModeProducts[0].product;
    var productSpecificationIds = firstProduct.productSpecificationIds!;

    var processResult = async (result: ProductSpecificationTreeResultData) => {
      for (var lineItem of editModeProducts) {
        lineItem.product.productSpecificationIds = [...result.selectedProductSpecificationIds];
      }
    }

    this.openProductSpecificationDialog(productSpecificationIds, firstProduct.productTypeId!, processResult);

  }

  onOpenProductSpecificationDialog(item: ProductLineItem) {

    var product = item.product;
    var processResult = async (result: ProductSpecificationTreeResultData) => {
      product.productSpecificationIds = [...result.selectedProductSpecificationIds];
    }

    this.openProductSpecificationDialog(product.productSpecificationIds!, product.productTypeId!, processResult);
  }

  openProductSpecificationDialog(productSpecificationIds: string[], productTypeIdFilter: string,
    processResult: (result: ProductSpecificationTreeResultData) => void) {
    const dialogWidth = '800px';
    const dialogHeight = '700px';

    this.productSpecificationTreeDialogRef = this.dialog.open(ProductSpecificationTreeDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: {
        productSpecificationIds: productSpecificationIds,
        productTypeIdFilter: productTypeIdFilter,
        productSpecifications: this.allProductSpecifications,
      }
    });

    this._subscriptions.push(this.productSpecificationTreeDialogRef.afterClosed()
      .subscribe((data: ProductSpecificationTreeResultData) => {
        if (!data) {
          // Dialog was closed without triggering workflow
          return;
        }

        processResult(data);
      })
    );
  }

  getWorkflowState(item: Product2) : string | undefined {
    if (!item.currentWorkflowState) {
      return undefined;
    }

    var stateInfo = this.allProductStates.find(x => x.state === item.currentWorkflowState?.state)
    return stateInfo?.name;
  }

  onBulkOpenWorkflowDialog() {

    var processResult = async (result: WorkflowTriggerResultData) => {

      var count = 0;
      var errorCount = 0;

      for (var lineItem of this.products) {

        if (lineItem.editMode)
        {
          var product = lineItem.product;
          var triggerResult = await this.productService.TriggerProductWorkflow(product.companyId!, product.id!, result.selectedTransition?.trigger, result.comments);

          count ++;
          if (triggerResult) {
            product.currentWorkflowState = triggerResult;
            if (lineItem.originalProduct) {
              lineItem.originalProduct.currentWorkflowState = triggerResult;
            }
          } else {
            errorCount++;
          }

          this.showMessage(`Workflow transitioned ${count} products with ${errorCount} errors!`);
        }
      }
    }

    // It should not be possible to get here unless all the workflow states are the same
    var workflowState = this.workflowStateOfProductsInEditModeAreTheSame();
    this.openWorkflowDialog(workflowState!, undefined, processResult);

  }

  onOpenWorkflowDialog(item: ProductLineItem) {

    var product = item.product;
    var processResult = async (result: WorkflowTriggerResultData) => {
      var triggerResult = await this.productService.TriggerProductWorkflow(product.companyId!, product.id!, result.selectedTransition?.trigger, result.comments);
      
      if (triggerResult) {
        var stateHasChanged = product.currentWorkflowState?.state !== triggerResult?.state;
        product.currentWorkflowState = triggerResult;
        if (item.originalProduct) {
          item.originalProduct.currentWorkflowState = triggerResult;
        }

        if (stateHasChanged) {
          this.showMessage(`Workflow successfully transitioned to ${triggerResult.name}!`);
        } else {
          this.showMessage(`Comment successfully added!`);
        }
      } else {
        this.showMessage("Failed to update workflow!");
      }
        
    }

    this.openWorkflowDialog(product.currentWorkflowState!, product, processResult);
  }

  openWorkflowDialog(workflowState: ProductWorkflow, workflowSubject: any | undefined,
    processResult: (result: WorkflowTriggerResultData) => void) {
    const dialogWidth = '800px';
    const dialogHeight = '500px';

    this.workflowTriggerDialogRef = this.dialog.open(WorkflowTriggerDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: {
        workflowSubject: workflowSubject,
        workflowState: workflowState,
      }
    });

    this._subscriptions.push(this.workflowTriggerDialogRef.afterClosed()
      .subscribe((data: WorkflowTriggerResultData) => {
        if (!data) {
          // Dialog was closed without triggering workflow
          return;
        }

        processResult(data);
      })
    );
  }
  
  workflowStateOfProductsInEditModeAreTheSame(): ProductWorkflow | undefined {
    // Filter the objects to find the selected ones
    const editModeProducts = this.products.filter(x => x.editMode);

    // Check if there are no selected objects
    if (editModeProducts.length === 0) {
        return undefined;
    }

    var firstWorkflowState = editModeProducts[0].product.currentWorkflowState;
    // Check if all workflow states are the same
    const allSame = editModeProducts.every(x => x.product.currentWorkflowState!.state === firstWorkflowState!.state);

    return allSame ? firstWorkflowState : undefined;
  }

  /*  Bulk Brand AutoComplete Filter */
  bulkBrandControl = new FormControl();
  allBrands: Required<Brand>[] = [];
  brandDirectory: Record<string, string> = {};
  filteredBulkBrands: Observable<Brand[]> | undefined;

  private loadBrands() {
    return this.situViewClient.listBrands(new ListBrandsRequest())
      .then((res: ListBrandsResponse) => {
        let brands = res.brands;
        this.allBrands = brands as Required<Brand>[] || [];
        this.allBrands.sort((a, b) => a.name.localeCompare(b.name));
        this.brandDirectory = {};
        this.allBrands.forEach(b => this.brandDirectory[b.id!] = b.name!);
      });
  }

  onBrandSelected(event: any, item: ProductLineItem): void {
    var brandName = event.option.value;
    item.product.brandId = this.allBrands?.find(x => x.name === brandName)?.id;
  }
  
  private filterBrands(searchTerm: string | undefined = undefined, supplierId?: string): ProductType[] {
    var brands = this.allBrands;

    if (supplierId) {
      brands = brands.filter(brand => brand.companyId === supplierId);
    }

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      brands = brands.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm!));
    }

    return brands;
  }

  onBulkBrandSelected(event: any) {
    var brandName = event.option.value;
    var brandId = this.allBrands.find(x => x.name === brandName)?.id;

    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.brandId = brandId;
        x.brandControl.setValue(brandName);
      }
    });
  }

  clearBulkBrand() {
    this.bulkBrandControl.reset();
  }

  onBulkQualityLevelChanged(event: any) {
    return this.products.forEach(x => {
      if (x.editMode) {
        let value = event.target.value
        x.product.qualityLevel = value ? Number(event.target.value) : undefined;
      }
    });
  }

  getCompanyName(item: Product2) : string | undefined {
    var company = this.allCompanies.find(x => x.id === item.companyId)
    return company?.name;
  }

  getProductTypeName(item: Product2) : string | undefined {
    var productType = this.allProductTypes.find(x => x.id === item.productTypeId)
    return productType?.name;
  }

  getProductSpecificationString(item: Product2) : string | undefined {
    if (!item.productSpecificationIds || !this.allProductSpecifications) {
      return undefined;
    }

    var specificationNames = this.allProductSpecifications
      .filter(x => item.productSpecificationIds?.includes(x.id!))
      .map(x => x.name);

    var specificationString = specificationNames.join(" ");
    return specificationString;
  }

  editAllLineItems(enable: boolean) {
    this.products.forEach(x => {
      if (x.editMode !== enable) {
        this.editLineItem(x, enable);
      }
    });
  }

  editLineItem(item: ProductLineItem, enable: boolean) {
    if (enable) {
      this.enableEditMode(item);
    } else {
      this.cancelEditMode(item);
    }
  }

  enableEditMode(item: ProductLineItem) {
    item.editMode = true;
    item.originalProduct = new Product2(item.product);

    item.brandControl.setValue(this.brandDirectory[item.product?.brandId ?? '']);

    item.filteredProductTypes = item.productTypeControl.valueChanges.pipe(
      map((value) => {
        return this.filterProductTypes(value);
      })
    );

    item.filteredBrands = item.brandControl.valueChanges.pipe(
      startWith(item.brandControl.value),
      map((input) => this.filterBrands(input, item.product.companyId)) 
    )
  
    // TODO see if there is a better way to do this without the timeout
    new Promise(resolve => setTimeout(resolve, 100)) // Hack, we need to wait for 'valueChanges.pipe' to be bound, it is not immediate 
    .then(() => {
      this.products.forEach(item => {
        item.productTypeNameFilter = this.allProductTypes?.find(x => x.id === item.product.productTypeId)?.name;
        item.productTypeControl.setValue(item.productTypeNameFilter);
        item.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
    });

    this.bulkCalloutsEnabled = undefined;
    this.bulkHideProductCode = undefined;
    this.bulkProductEnabled = undefined;
  }

  cancelEditMode(item: ProductLineItem) {
    item.editMode = false;
    if (item.originalProduct) {
      item.product = item.originalProduct;
      item.originalProduct = undefined;
    }
  }

  onLineItemProductTypeSelected(event: any, lineItem: ProductLineItem): void {
    var productTypeName = event.option.value;
    lineItem.product.productTypeId = this.allProductTypes?.find(x => x.name === productTypeName)?.id;
    lineItem.productTypeNameFilter = productTypeName;
  }

  hasEditModeLineItems(): boolean {
    if (!this.products) {
      return false;
    }

    return this.products.some(x => x.editMode);
  }

  countEditModeLineItems(): number {
    var count = 0;
    if (this.products) {
      
      for (var product of this.products) {
        if (product.editMode) {
          count++;
        }
      }
    }
    
    return count;
  }

  editSameProductType(productTypeId: string, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.productTypeId === productTypeId) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameProductSpecification(productSpecificationIds: string[] | undefined, enable: boolean) {
    this.products.forEach(x => {
      var hasSameSpecifications = productSpecificationIds &&
        x.product.productSpecificationIds &&
        productSpecificationIds.length === x.product.productSpecificationIds.length &&
        productSpecificationIds.every((value, index) => value === x.product.productSpecificationIds![index]);

      if (hasSameSpecifications) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameDescription(description: string, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.description === description) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameBrand(brandId: string, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.brandId === brandId) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameQualityLevel(qualityLevel: QualityLevel, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.qualityLevel === qualityLevel) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameProductWorkflowState(productState: ProductWorkflowState, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.currentWorkflowState!.state === productState) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameNotes(notes: string, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.notes === notes) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameCalloutsEnabled(calloutsEnabled: boolean, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.calloutsEnabled === calloutsEnabled) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameHideSku(hideSku: boolean, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.hideSku === hideSku) {
        this.editLineItem(x, enable)
      }
    });
  }

  editSameProductDisabled(disabled: boolean, enable: boolean) {
    this.products.forEach(x => {
      if (x.product.disabled === disabled) {
        this.editLineItem(x, enable)
      }
    });
  }
  
  private loadProductSpecifications() {
    return this.productService.LoadProductSpecifications()
      .then((ProductSpecifications: ProductSpecification[]) => {
        this.allProductSpecifications = ProductSpecifications;
      });
  }

  /*  Company AutoComplete Filter */
  companyControl = new FormControl();
  filteredCompanies: Observable<Company[]> | undefined;
  allCompanies: Company[] = [];
  companyDirectory: Record<string, string> = {};

  private loadCompanies() {
    return this.companyViewService.LoadCompanies()
      .then((companies: Company[]) => {
        this.allCompanies = companies;
        companies.forEach(c => this.companyDirectory[c.id!] = c.name!);
        
        this.companyNameFilter = this.allCompanies?.find(x => x.id === this.companyIdFilter)?.name;
        this.companyControl.setValue(this.companyNameFilter);
        this.companyControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });

        this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
        this.productStateControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
  }

  onSaveLineItem(item: ProductLineItem) {
    this.saveLineItem(item);
  }
  
  async onSaveAllEditModeLineItems() {
    this.loading = true;

    var updateCount = 0;
    var failCount = 0;

    try {
      for (let productLineItem of this.products) {
        if (!productLineItem.editMode) {
          continue;
        }

        var updatedProduct = await this.productService.UpdateProduct(productLineItem.product);

        if (updatedProduct) {
          this.updateExistingLineItem(updatedProduct);
          updateCount++;
        } else {
          failCount++;
          this.showMessage("There was an issue updating the products.");
        }
      }
    } finally {
      this.loading = false;
    }

    
    this.showMessage(`${updateCount} Products have been updated with ${failCount} errors.`);
  }
  
  saveLineItem(item: ProductLineItem) {
    this.loading = true;

    this.productService.UpdateProduct(item.product)
    .then((product: Product2 | undefined) => {
      if (product) {
        this.updateExistingLineItem(product);
        this.showMessage("The Product has been updated");
      } else {
        this.showMessage("There was an issue updating the product.");
      }
    })
    .finally(() => {
      this.loading = false;
    });

  }
  
  updateExistingLineItems(updatedProducts: Product2[]) {
    updatedProducts.forEach(x => this.updateExistingLineItem(x));
  }

  updateExistingLineItem(updatedProduct: Product2) {
    var existingLineItem = this.products.find(y => y.product.id === updatedProduct.id);
    if (existingLineItem) {
      existingLineItem.product = updatedProduct;
      existingLineItem.editMode = false;
    } else {
      this.products.push(new ProductLineItem(updatedProduct));
    }
  }

  onCompanySelected(event: any): void {
    this.selectCompanyByName(event.option.value);
  }

  selectCompanyByName(companyName: string) {
    this.companyIdFilter = this.allCompanies?.find(x => x.name === companyName)?.id;
    this.companyNameFilter = companyName;
  }

  selectCompanyById(companyId: string) {
    this.companyIdFilter = companyId;
    this.companyNameFilter = this.allCompanies?.find(x => x.id === companyId)?.name;
  }

  selectCompanyByProduct(productCompanyId: string) {
    this.companyIdFilter = productCompanyId;
    this.companyNameFilter = this.allCompanies?.find(x => x.id === productCompanyId)?.name;

    this.companyControl.setValue(this.companyNameFilter);
    this.companyControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onClearCompany() {
    this.clearCompany();
  }

  clearCompany() {
    this.companyControl.reset();
    this.companyIdFilter = undefined;
    this.companyNameFilter = undefined;
  }

  private filterCompanies(searchTerm: string | undefined = undefined): Company[] {
    var companies = this.allCompanies;

    // Filter Company Names by the supplied Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      companies = companies.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return companies;
  }
  /*  Company AutoComplete Filter */
    
  /*  ProductType AutoComplete Filter */
  productTypeControl = new FormControl();
  filteredProductTypes: Observable<ProductType[]> | undefined;
  allProductTypes: ProductType[] = [];
  
  private loadProductTypes() {
    return this.productService.LoadProductTypes()
      .then((productTypes: ProductType[]) => {
        this.allProductTypes = productTypes;
        
        this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === this.productTypeFilter)?.name;
        this.productTypeControl.setValue(this.productTypeNameFilter);
        this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
  }

  onProductTypeSelected(event: any): void {
    this.selectProductTypeByName(event.option.value);
  }

  selectProductTypeByName(productTypeName: string) {
    this.productTypeFilter = this.allProductTypes?.find(x => x.name === productTypeName)?.id;
    this.productTypeNameFilter = productTypeName;
  }

  selectProductTypeById(productTypeId: string) {
    this.productTypeFilter = productTypeId;
    this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === productTypeId)?.name;
  }

  selectProductTypeByProduct(productTypeId: string) {
    this.productTypeFilter = productTypeId;
    this.productTypeNameFilter = this.allProductTypes?.find(x => x.id === productTypeId)?.name;

    this.productTypeControl.setValue(this.productTypeNameFilter);
    this.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  onClearProductType() {
    this.clearProductType();
  }

  clearProductType() {
    this.productTypeControl.reset();
    this.productTypeFilter = undefined;
    this.productTypeNameFilter = undefined;
  }

  private filterProductTypes(searchTerm: string | undefined = undefined): ProductType[] {
    var productTypes = this.allProductTypes;

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productTypes = productTypes.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return productTypes;
  }
  /*  ProductType AutoComplete Filter */

  /*  ProductState AutoComplete Filter */
  productStateControl = new FormControl();
  filteredProductStates: Observable<ProductWorkflow[]> | undefined;
  allProductStates: ProductWorkflow[] = [];
  
  private loadProductStates() {
    return this.productService.LoadProductWorkflowConfiguration()
      .then((productWorkflow: ProductWorkflow[]) => {
        this.allProductStates = productWorkflow;
        
        this.productStateNameFilter = this.allProductStates?.find(x => x.state === this.productStateFilter)?.name;
        this.productStateControl.setValue(this.productStateNameFilter);
        this.productStateControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      });
  }

  onProductStateSelected(event: any): void {
    this.selectProductStateByName(event.option.value);
  }

  selectProductState(productState: ProductWorkflowState) {
    this.productStateFilter = productState;
    this.productStateNameFilter = this.allProductStates?.find(x => x.state === productState)?.name;
  }

  selectProductStateByName(productStateName: string) {
    this.productStateFilter = this.allProductStates?.find(x => x.name === productStateName)?.state;
    this.productStateNameFilter = productStateName;
  }

  onClearProductState() {
    this.clearProductState();
  }

  clearProductState() {
    this.productStateControl.reset();
    this.productStateFilter = undefined;
    this.productStateNameFilter = undefined;
  }

  private filterProductStates(searchTerm: string | undefined = undefined): ProductWorkflow[] {
    var productStates = this.allProductStates;

    // Filter by Search Terms
    if (searchTerm) {
      searchTerm = searchTerm.toLowerCase();
      productStates = productStates.filter(x => x.name).filter(state => state.name!.toLowerCase().includes(searchTerm!));
    }

    return productStates;
  }

  /*  Bulk Product Type AutoComplete Filter */
  bulkProductTypeControl = new FormControl();
  filteredBulkProductTypes: Observable<ProductType[]> | undefined;

  onBulkProductTypeSelected(event: any) {
    var productTypeName = event.option.value;
    var productTypeId = this.allProductTypes?.find(x => x.name === productTypeName)?.id;

    return this.products.forEach(x => {
      if (x.editMode) {
      x.productTypeNameFilter = productTypeName;
      x.product.productTypeId = productTypeId;
      x.productTypeControl.setValue(x.productTypeNameFilter);
      x.productTypeControl.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      }
    });
  }

  clearBulkProductType() {
    this.bulkProductTypeControl.reset();
    this.bulkProductTypeIdFilter = undefined;
    this.bulkProductTypeNameFilter = undefined;
  }

  /*  Bulk Product Type AutoComplete Filter */

  onBulkDescriptionChanged(event: any) {
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.description = event.target.value;
      }
    });
  }
    
  onBulkBrandChanged(event: any) {
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.brand = event.target.value;
      }
    });
  }

  onBulkNotesChanged(event: any) {
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.notes = event.target.value;
      }
    });
  }

  onBulkCalloutsEnabledChanged(value: boolean) {
    this.bulkCalloutsEnabled = value;
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.calloutsEnabled = value;
      }
    });
  }

  onBulkHideProductCodeChanged(value: boolean) {
    this.bulkHideProductCode = value;
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.hideSku = value;
      }
    });
  }

  onBulkProductEnabledChanged(value: boolean) {
    this.bulkProductEnabled = value;
    return this.products.forEach(x => {
      if (x.editMode) {
        x.product.disabled = !value;
      }
    });
  }

  /*  ProductState AutoComplete Filter */

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

class ProductLineItem {
  product: Product2;
  originalProduct: Product2 | undefined;

  editMode = false;
  
  productTypeControl = new FormControl();
  filteredProductTypes: Observable<ProductType[]> | undefined;
  productTypeNameFilter: string | undefined;

  brandControl = new FormControl();
  filteredBrands: Observable<Brand[]> | undefined;

  constructor(product: Product2) {
    this.product = product;
  }

  clearProductType() {
    this.productTypeControl.reset();
    this.brandControl.reset();
    this.product.productTypeId = undefined;
    this.productTypeNameFilter = undefined;
  }

  clearBrand() {
    this.brandControl.reset();
    this.product.brandId = undefined;
  }

}